<template>
	<div class="import-shop-products">
		<b-loading :is-full-page="true" :active.sync="isLoading" :can-cancel="false"></b-loading>
		<h1>Product Price Adjustment</h1>

		<p class="description">
			This page will import the CSV for adjusting the price. Please refer to product.price_adjustment.csv file provided.
		</p>

		<b-field class="file">
			<b-upload v-model="file" ref="file">
				<a class="button is-primary">
					<b-icon icon="upload"></b-icon>
					<span>Select a file</span>
				</a>
			</b-upload>
			<span class="file-name" v-if="file">
				{{ file.name }}
			</span>
		</b-field>

		<div class="button-container">
			<div class="big-button" @click="uploadFile()">
				Start Price Adjustment
			</div>
		</div>

		<br />
		<br />

		<div class="">
			<b-field label="Select Brand (optional)">
				<b-autocomplete
					v-model="brandSuggestions"
					:data="filteredBrands"
					:open-on-focus="true"
					placeholder="Brand"
					field="code"
					@select="option => (selectedBrand = option)"
					class="brandAutocomplete"
				>
					<template slot="empty">No results found</template>
				</b-autocomplete>
			</b-field>

			<div v-if="selectedBrand && selectedBrand._id">
				Total Products for <strong>{{ selectedBrand.code }}: {{ totalCount }} </strong>
			</div>

			<div class="single-text-wrapper">
				<b-field label="Percentage">
					<b-input v-model="percentage" placeholder="20%" @keyup.native.enter="confirmPercentage()"></b-input>
				</b-field>
			</div>

			<div class="button-container">
				<div class="big-button" @click="confirmPercentage()">
					Update Percentage
				</div>
			</div>
			<br />
			<br />
			<div v-if="processCount > 0">Updating {{ processCount }} of {{ total }}</div>
			<div v-if="failCount > 0">{{ failCount }} FAILED</div>
			<br />
			<br />
			<div v-if="updateResult.length > 0">
				<div v-for="(data, index) of updateResult" v-bind:key="index">{{ data }}</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'ImportShopProducts',
	data: () => ({
		isLoading: false,
		file: null,
		fileUpload: null,
		brands: [], // To store brands
		selectedBrand: null,
		brandSuggestions: '',
		percentage: '',
		products: [],
		totalLoop: 0,
		total: 0,
		totalCount: 0,
		loopCount: 0,
		limit: 100,
		updateResult: [],
		processCount: 0,
		failCount: 0
	}),

	computed: {
		filteredBrands() {
			return this.brands.filter(option => {
				return (
					option.code
						.toString()
						.toLowerCase()
						.indexOf(this.brandSuggestions.toLowerCase()) >= 0
				)
			})
		}
	},

	mounted: async function() {
		await this.getBrands()
	},

	watch: {
		fileUpload: async function() {
			await this.importProducts()
		},

		selectedBrand: function(data) {
			if (data._id) {
				this.getBrandTotalItem()
			}
		}
	},

	methods: {
		// To get total number of products in selected brand
		getBrandTotalItem: async function() {
			this.totalCount = 0
			var data = {
				limit: 1,
				brand: this.selectedBrand._id
			}

			const apiExportProducts = await this.$api.getProducts({ data })

			this.totalCount = apiExportProducts.data.total
		},

		// Update selected brand percentages
		updatePercentage: async function() {
			this.processCount = 0
			this.failCount = 0
			this.isLoading = true
			const compo = this
			this.updateResult = []

			// Get total number of products
			var data = {
				limit: 1,
				brand: this.selectedBrand._id
			}

			const apiExportProducts = await this.$api.getProducts({ data })

			this.total = apiExportProducts.data.total
			this.totalLoop = Math.ceil(apiExportProducts.data.total / this.limit)

			// Get loop count
			if (apiExportProducts !== undefined && apiExportProducts.data.total > 0) {
				this.totalCount = apiExportProducts.data.total
			} else {
				this.isLoading = false
				this.$swal.fire({
					icon: 'info',
					title: 'Oops...',
					text: 'Nothing found!'
				})

				return
			}

			var i
			var skip
			const parallel = []

			// Get product details
			for (i = 0; i < this.totalLoop; i++) {
				skip = i * this.limit
				data = {
					limit: this.limit,
					brand: this.selectedBrand._id,
					sort: {
						createdAt: 'desc'
					},
					skip
				}

				var result = await this.getProductsLoop(data)
				parallel.push(result)
			}

			const total_result = await Promise.all(parallel)

			const total_products = []

			total_result.map(result => {
				const { list } = result.data

				list.map(product => {
					total_products.push(product)
				})
			})

			total_products.sort((a, b) => {
				const aTime = compo
					.$moment(a.createdAt)
					.toDate()
					.getTime()
				const bTime = compo
					.$moment(b.createdAt)
					.toDate()
					.getTime()

				return aTime - bTime
			})

			// Loop every product from selected brand and update input percentages
			for (const product of total_products) {
				let data = {
					id: product._id,
					commission: this.percentage
				}

				const apiProductUpdate = await this.$api.productUpdate({ data })

				if (apiProductUpdate.status == 204) {
					const message = product.bd_code + ' has been updated to ' + this.percentage + '%'
					this.updateResult.push(message)
				} else {
					const message = product.code + 'ERROR!!!'
					this.updateResult.push(message)
					this.failCount++
				}

				this.processCount++
			}

			this.isLoading = false
		},

		getProductsLoop: async function(data) {
			return await this.$api.getProducts({ data })
		},

		confirmPercentage: async function() {
			if (this.percentage == '' || !this.selectedBrand) {
				this.$swal.fire({
					icon: 'error',
					title: 'Oops...',
					text: 'Brand and Percentage are required!'
				})

				return
			}

			if (isNaN(this.percentage)) {
				this.$swal.fire({
					icon: 'error',
					title: 'Oops...',
					text: 'Percentage can only be number'
				})

				return
			}

			if (this.percentage < 10) {
				this.$swal.fire({
					icon: 'error',
					title: 'Oops...',
					text: 'Please check percentage value'
				})

				return
			}

			this.$swal
				.fire({
					title: this.selectedBrand.name + ' to ' + this.percentage + '%?',
					text: 'Are you sure you want to update?',
					icon: 'warning',
					showCancelButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Yes!'
				})
				.then(result => {
					if (result.value) {
						this.updatePercentage()
					}
				})
		},

		getBrands: async function() {
			const data = {
				limit: 2000,
				sort: {
					name: 'asc'
				}
			}
			const apiGetBrands = await this.$api.getBrand({ data })

			if (apiGetBrands !== undefined) {
				this.brands = apiGetBrands.data.list
			}
		},
		// Import Shop Products
		importProducts: async function() {
			const data = {
				file: this.fileUpload._id
			}

			const apiImportShopProducts = await this.$api.importProduct({
				data
			})

			if (apiImportShopProducts !== undefined) {
				this.$swal.fire('Product Price are adjusted!')
			}
			this.isLoading = false
		},

		uploadFile: async function() {
			this.isLoading = true
			const data = {
				type: 'csv',
				file: this.file
			}

			const apiFileUpload = await this.$api.fileUpload({ data })

			if (apiFileUpload !== undefined) {
				this.fileUpload = apiFileUpload.data
			}
		}
	}
}
</script>

<style scoped>
@import '../styles/GeneralStyles.css';
</style>
